import {Graphics, Sprite} from "pixi.js";
import {Polygon} from "detect-collisions";
import {lerp, round} from "../utils.js";
import {logger} from "../i.js";
import {getTexture} from "../assets.js";
import {isVectorable, Vector} from "../i.js";
import {Random} from "../i.js"; // ../random.js
import {sq} from "../i.js"; // ./dump.js
import {ConfigStructure, OptionalProperty, RequiredProperty} from "../i.js"; // ../validators.js
import {Density} from "../i.js"; // ../density.js
import {Particles} from "../i.js";
import {AI, AIUtils} from "../i.js";
import {FACTIONS, Thing, STANDARD_PPF, THING_TYPES, Z_INDICES} from "../i.js";
import {DropList, ITEMS} from "../i.js";
import {Wave} from "../i.js";
import {Axon, selectAxonLifespan} from "../i.js";
import {ACHIEVEMENTS} from "../i.js";

let _plateAngle = Math.atan(2);
let _plateCenterX = 0.60 / 4;
let _plateCenterY = 0.30 / 4;
const PLATE_INFO = new Map([
	["--", {
		origin: new Vector(- _plateCenterX, - _plateCenterY),
		normal: Math.PI + _plateAngle,
		length: Math.hypot(0.3, 0.15)
	}],
	["-+", {
		origin: new Vector(- _plateCenterX, _plateCenterY),
		normal: Math.PI - _plateAngle,
		length: Math.hypot(0.3, 0.15)
	}],
	["+-", {
		origin: new Vector(_plateCenterX, - _plateCenterY),
		normal: - _plateAngle,
		length: Math.hypot(0.3, 0.15)
	}],
	["++", {
		origin: new Vector(_plateCenterX, _plateCenterY),
		normal: _plateAngle,
		length: Math.hypot(0.3, 0.15)
	}]
]);

// Desired minimum distance between compasses, f
const DESIRED_DISTANCE = 1;
// Max speed of a compass, f/s
const MAX_SPEED = 0.2;
// Friction, 1/s
const FRICTION = 2;

const CHANCE_AXON_ATTEMPT_SUCCEEDS = 0.5;
// Cooldown between axon actions, in seconds
// After an axon going to/from this compass is added/removed, wait for this cooldown
// before sending out a new outgoing axon. Should be very short! Just so that multiple
// actions don't happen instantly.
const AXON_ACTION_COOLDOWN = 0.2;

const TIME_TO_CHARGE_WAVES = 2;

// Visuals

// How long to display the damage effect, in seconds
const DAMAGE_EFFECT_DURATION = 0.3;



class CompassAI extends AI {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// Time to the next axon attempt, defaults to 0 if omitted
		new OptionalProperty("timeToAxonAttempt", v => Number.isFinite(v) && v >= 0)
	]);

	constructor(thing, config) {
		CompassAI.CONFIG_STRUCTURE.test(config);

		super(thing);

		this._timeToAxonAttempt = config.timeToAxonAttempt ?? 0;
	}

	move(dt, time) {
		let tacticalReport = this.thing.space.requestTacticalReport(
			entry => (entry.name === "Compass"),
			"COMPASS-REPEL"
		).filter(
			entry => (Vector.diff(entry.pos, this.thing.pos).mag() <= 1)
		);

		let acc = new Vector();
		for (let entry of tacticalReport) {
			acc.add(AIUtils.getRepelAcc(
				this.thing,
				entry,
				MAX_SPEED * FRICTION,
				DESIRED_DISTANCE
			));
		}
		// Apply upper bound to acceleration
		acc.norm(Math.max(acc.mag(), MAX_SPEED * FRICTION));

		AIUtils.translate(this.thing, acc, FRICTION, dt);

		this._manageWaves(dt, time);
		this._manageAxons(dt, time);
	}

	// Record that an axon-related action just happened
	// This activates a cooldown
	recordAxonAction() {
		this._timeToAxonAttempt = Math.max(this._timeToAxonAttempt, AXON_ACTION_COOLDOWN);
	}

	_manageWaves(dt, time) {
		if (!this.thing.isCharging) return;

		this.thing.fractionCharged += dt / TIME_TO_CHARGE_WAVES;

		if (this.thing.fractionCharged >= 1) {
			PLATE_INFO.forEach(plate =>
				this.thing.space.requestSpawn(Wave, {
					pos: new Vector(plate.origin)
						.rotateBy(this.thing.rot)
						.add(this.thing.pos),
					rot: plate.normal + this.thing.rot
				})
			);
			this.thing.fractionCharged = 0;
			this.thing.isCharging = false;
		}
	}

	_manageAxons(dt, time) {
		this._timeToAxonAttempt -= dt;
		this._timeToAxonAttempt = Math.max(this._timeToAxonAttempt, 0);
		if ((this.thing.outgoingAxonId === null) && (this._timeToAxonAttempt <= 0)) {
			if (Math.random() > CHANCE_AXON_ATTEMPT_SUCCEEDS) {
				this._timeToAxonAttempt += selectAxonLifespan();
				return;
			}

			let tacticalReport = this.thing.space.requestTacticalReport(
				entry => (entry.name === "Compass"),
				"COMPASS"
			).filter(
				entry => (
					(entry.id !== this.thing.id)
						&& (Vector.diff(entry.pos, this.thing.pos).mag() <= 4)
						&& !this._hasAxonFrom(entry.id)
				)
			);

			if (tacticalReport.length === 0) {
				this._timeToAxonAttempt += selectAxonLifespan();
				return;
			}

			// Sort the tactical report from closest to furthest
			tacticalReport.sort((entryA, entryB) => {
				let distA = Vector.diff(entryA.pos, this.thing.pos).mag();
				entryA.dist = distA;
				let distB = Vector.diff(entryB.pos, this.thing.pos).mag();
				entryB.dist = distB;
				return distA - distB;
			});

			let weights;
			if (tacticalReport.length === 1) {
				weights = [1];
			} else if (tacticalReport.length === 2) {
				weights = [3, 2];
			} else {
				weights = [3, 2, 1];
			}
			let chosenEntry = tacticalReport[Random.weighted(weights)];

			let axon = this.thing.space.requestSpawn(Axon, {
				firstParentId: this.thing.id,
				secondParentId: chosenEntry.id
			});
		}
	}

	// Return true iff an axon goes from otherCompassId to this compass
	_hasAxonFrom(otherCompassId) {
		return Array.from(this.thing.incomingAxonIds.values()).includes(otherCompassId);
	}
}



export function drawPlate(display, origin, normal, length, offset, time) {
	display.clear();

	let yBody = 0;
	let yWave = - offset - 0.02;

	if (offset > 0) {
		let numConnections = Math.max(Math.round(20 * length), 2);
		for (let i = 0; i < numConnections; ++i) {
			let xMin = lerp(- length / 2, length / 2, i / numConnections);
			let xMax = xMin + length / numConnections;
			let getX = sliceOffset => lerp(
				xMin, xMax,
				(Random.noise(100 * xMin + sliceOffset, 8 * time) + 1) / 2
			)
			let xBody = getX(0);
			let xMid = getX(25);
			let xWave = getX(50);
			display
				.moveTo(xBody, yBody)
				.lineTo(xMid, (yBody + yWave) / 2)
				.lineTo(xWave, yWave)
				.stroke({
					color: {h: 10, s: 100, v: 100},
					width: 0.015,
					cap: "round"
				});
		}
	}

	display
		.moveTo(- length / 2 + 0.02, yWave)
		.lineTo(length / 2 - 0.02, yWave)
		.stroke({
			color: {h: 0, s: 70, v: 40},
			width: 0.04,
			cap: "round"
		});
	display.rotation = normal;
	display.position.copyFrom(Vector.rotateBy(origin, - Math.PI / 2));
}



class CompassAnimation {
	constructor(thing) {
		this.thing = thing;

		this._timeSinceLastDamage = Infinity;

		this._plates = new Map();
		for (let plateName of ["--", "-+", "+-", "++"]) {
			let plate = new Graphics();
			let plateInfo = PLATE_INFO.get(plateName);
			drawPlate(plate, plateInfo.origin, plateInfo.normal, plateInfo.length, 0, 0);
			this.thing.display.addChild(plate);
			this._plates.set(plateName, plate);
		}

		this._body = new Sprite(getTexture("images/desolation/compass.svg"));
		this._body.scale.set(1 / STANDARD_PPF);
		this._body.anchor.set(0.5, 0.5);
		this._damage = new Sprite(getTexture("images/desolation/compass-damage.svg"));
		this._damage.scale.set(1 / STANDARD_PPF);
		this._damage.anchor.set(0.5, 0.5);
		this._damage.visible = false;
		this.thing.display.addChild(this._body, this._damage);
	}

	update(dt, time) {
		// TODO: This timer won't increment if the thing is off-screen
		this._timeSinceLastDamage += dt;
		if (this._timeSinceLastDamage > DAMAGE_EFFECT_DURATION) {
			this._damage.visible = false;
		} else {
			this._damage.visible = true;
			this._damage.alpha = Math.min(
				1,
				2 * (1 - this._timeSinceLastDamage / DAMAGE_EFFECT_DURATION)
			);
		}

		let f = sq(this.thing.fractionCharged);
		let offset = 0.1 * 4 * f * (1 - f);
		this._plates.forEach((plate, plateName) => {
			let plateInfo = PLATE_INFO.get(plateName);
			drawPlate(
				plate, plateInfo.origin, plateInfo.normal, plateInfo.length, offset, time
			);
		});
	}

	recordDamage(isFromAxon) {
		this._timeSinceLastDamage = 0;

		// TODO: This will happen even if the compass is off-screen, which is bad for
		// performance
		this.thing.space.particles.add(
			Particles.TYPES.SPARK,
			this.thing.pos,
			null,
			isFromAxon ? 20 : 5
		);
	}
}



const COMPASS_DROPLIST = new DropList([
	[ITEMS.DENDRITIC_RECEIVER,  [1, 2, 3, 4, 5], [0.1, 0.1, 0.05, 0.05, 0.02]],
	[ITEMS.AFFINE_ADDER,        [1],             [0.5]                       ],
	[ITEMS.NONLINEAR_ACTIVATOR, [1],             [0.4]                       ],
	[ITEMS.AXONIC_EMITTER,      [1],             [0.3]                       ],
	[ITEMS.WAVE_PLATE,          [1, 2, 3, 4],    [0.1, 0.1, 0.05, 0.02]      ]
], 1);



export class Compass extends Thing {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// pos of this compass, a vector with values in flares
		new RequiredProperty("pos", isVectorable),
		// vel of this compass, a vector with values in flares / second
		new OptionalProperty("vel", isVectorable),
		// If specified: charging, this number is fraction charged, 0 to 1 inclusive.
		// (I considered making 1 exclusive, if the fraction is exactly 1 then the compass
		// instantly fires waves and resets the fraction to 0. But when rounding, fraction
		// can become exactly 1.)
		// If omitted: not charging.
		new OptionalProperty("charge", v => Number.isFinite(v) && v >= 0 && v <= 1)

		// AI state: timeToAxonAttempt
	]);

	// Create a new compass
	// config is an object with the properties provided by the spawn request
	// enrichment, plus the properties specified in CONFIG_STRUCTURE above.
	constructor(config) {
		Compass.CONFIG_STRUCTURE.test(config);

		// Fill in missing config fields
		config.name = "Compass";
		config.zIndex = Z_INDICES.NPC;
		config.len = 0.60;
		config.hitbox = new Polygon({x: 0, y: 0}, [
			{x:  0.30, y:     0},
			{x:     0, y: -0.15},
			{x: -0.30, y:     0},
			{x:     0, y:  0.15}
		]);
		config.pos = new Vector(config.pos);
		config.vel = new Vector(config.vel);
		config.rot = config.space.compassDirection;
		config.maxHealth = 39;
		config.type = THING_TYPES.SPACESHIP;
		config.faction = FACTIONS.DAMSEL;
		config.canDespawn = true;
		config.guesstimationWeight = 1;
		config.despawnSafetyWindow = 3;
		config.pool = Density.POOLS.STATIC;

		// Call parent constructor
		super(config);

		this.isCharging = config.charge !== undefined;
		this.fractionCharged = config.charge ?? 0;
		// id of the outgoing axon, or null if no axon
		this.outgoingAxonId = null;
		// Maps ids of incoming axons to ids of their firstParent compasses
		// The ids of firstParent compasses are only needed to make sure we never create
		// axons from A to B and from B to A at the same time.
		this.incomingAxonIds = new Map();

		// TODO: Probably just make the AI state a single "ai" property
		// But that should really be a game-wide decision
		let aiConfig = {};
		if (config.timeToAxonAttempt !== undefined) {
			aiConfig.timeToAxonAttempt = config.timeToAxonAttempt;
		}
		this.ai = new CompassAI(this, aiConfig);
		this.animation = new CompassAnimation(this);
	}

	save() {
		let config = super.save();

		config.pos = this.pos.save();
		// TODO: vel is usually [0, 0], find a way to easily omit it in that case
		config.vel = this.vel.save();
		if (this.isCharging) config.charge = round(this.fractionCharged);

		// AI state

		config.timeToAxonAttempt = round(this.ai._timeToAxonAttempt);

		return config;
	}

	// Called by Axon constructor
	// Why do this roundabout thing instead of just saving the id when a compass creates
	// the outbound axon? Because this way we don't have to save the outgoing axon id!
	addOutgoingAxon(axonId) {
		if (this.outgoingAxonId !== null) {
			logger.warning(
				"axon.js",
				"attempting to add an outgoing axon when one is already present"
			);
			return;
		}
		this.outgoingAxonId = axonId;
		this.ai.recordAxonAction();
	}

	// Called by Axon AI
	removeOutgoingAxon() {
		if (this.outgoingAxonId === null) {
			logger.warning("axon.js", "attempting to remove unknown outgoing axon");
			return;
		}
		this.outgoingAxonId = null;
		this.ai.recordAxonAction();
	}

	// Called by Axon constructor
	addIncomingAxon(axonId, firstParentId) {
		this.incomingAxonIds.set(axonId, firstParentId);
		this.ai.recordAxonAction();
	}

	// Called by Axon AI
	removeIncomingAxon(id) {
		if (!this.incomingAxonIds.has(id)) {
			logger.warning("compass.js", "attempting to remove unknown incoming axon");
			return;
		}
		this.incomingAxonIds.delete(id);
		this.ai.recordAxonAction();
	}

	// Receive a signal from an axon
	receiveSignal() {
		this.isCharging = true;
	}

	// Receive burn from an axon
	receiveBurn(source) {
		if (source.identity !== "AXON") {
			logger.warning("compass.js", "receiving burn from a non-axon");
			return;
		}
		this.takeDamage(Math.ceil(this.maxHealth / 2), true, source);
	}

	// Take damage from an attack
	// Args:
	//   damage    - number
	//   dropItems - is an optional boolean (defaults to true if omitted).
	//   source    - the thing that is the source of the damage
	//
	// TODO: `source` is a non-standard signature. Think about a generic approach to this
	// kind of situation.
	takeDamage(damage, dropItems, source) {
		// Call animation first so that particles are emitted on shatter
		this.animation.recordDamage(source?.identity === "AXON");
		if (this.health > damage) {
			this.health -= damage;
		} else {
			this.space.game.unicenter.ship.achievements.recordAchievement(
				ACHIEVEMENTS.COMPASS
			);
			this.prepareToDie(dropItems);
			return;
		}

		// Avoid a chain reaction: only send signals on non-burn damage
		if (source?.identity !== "AXON") {
			// Send out signals
			let axon = this.space.requestThingById(this.outgoingAxonId);
			if (axon !== null) axon.receiveSignal("forward");
			this.incomingAxonIds.forEach((firstParentId, axonId) => {
				let axon = this.space.requestThingById(axonId);
				if (axon !== null) axon.receiveSignal("backward");
			});

			// Start charging waves
			this.isCharging = true;
		}
	}

	prepareToDie(dropItems) {
		if (dropItems) {
			this.space.requestDrop(
				this.pos,
				this.radius,
				this.vel,
				COMPASS_DROPLIST
			);
		}
		super.prepareToDie(dropItems);
	}
};
